import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LanguageSelector } from '../components/LanguageSelector';
import { Loader2, Mail, Lock, User, ArrowLeft, Key, CheckCircle, Phone, MessageCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getDeviceId } from '../utils/deviceId';
import { isInAppBrowser, getInAppBrowserName } from '../utils/browserDetect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    preferredLanguage: 'en',
    activationCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/contact`).then(res => setContactSettings(res.data)).catch(() => {});
  }, []);

  const whatsappUrl = contactSettings 
    ? `https://wa.me/${contactSettings.whatsapp_number}?text=${encodeURIComponent(contactSettings.whatsapp_message || '')}`
    : '#';
  const phoneUrl = contactSettings ? `tel:${contactSettings.phone_number}` : '#';
  const phoneDisplay = contactSettings?.phone_display || '';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!formData.activationCode.trim()) {
      toast.error('Activation code is required');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        preferred_language: formData.preferredLanguage,
        activation_code: formData.activationCode.trim(),
        device_id: getDeviceId()
      });
      
      // If we get here with 202, it means pending approval
      setPendingApproval(true);
    } catch (error) {
      if (error.response?.status === 202) {
        // 202 Accepted = Pending approval
        setPendingApproval(true);
        toast.success('Registration submitted! Awaiting admin approval.');
      } else {
        toast.error(error.response?.data?.detail || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const isWebView = isInAppBrowser();

  const handleGoogleLogin = () => {
    // Validate activation code is entered
    if (!formData.activationCode.trim()) {
      toast.error('Please enter an activation code before signing up with Google');
      return;
    }
    if (isWebView) {
      toast.error(`Google Sign-In is blocked in ${getInAppBrowserName()}. Please open in Chrome or Safari.`);
      return;
    }
    
    // Store activation code in sessionStorage for Google OAuth flow
    sessionStorage.setItem('egns_activation_code', formData.activationCode.trim());
    
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to dashboard (main app) - Emergent Auth will append #session_id to this URL
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Show pending approval screen
  if (pendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="pending-approval-page">
        <div className="absolute inset-0 hero-pattern opacity-30" />
        
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700/50 bg-card/95 backdrop-blur text-center">
          <CardHeader>
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-teal-500/20 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-teal-500" />
            </div>
            <CardTitle className="font-heading text-2xl">Registration Submitted!</CardTitle>
            <CardDescription className="text-base">
              Your account is pending admin approval
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-slate-800/50 rounded-lg p-4 text-left">
              <p className="text-sm text-muted-foreground mb-2">What happens next:</p>
              <ul className="text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-teal-500">1.</span>
                  <span>An admin will review your registration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500">2.</span>
                  <span>You'll receive an email once approved</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500">3.</span>
                  <span>After approval, you can log in and start learning!</span>
                </li>
              </ul>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Email: <span className="text-foreground">{formData.email}</span>
            </p>
          </CardContent>
          
          <CardFooter className="flex justify-center">
            <Link to="/login">
              <Button variant="outline">
                Go to Login
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="register-page">
      {/* Background pattern */}
      <div className="absolute inset-0 hero-pattern opacity-30" />
      
      {/* Back button */}
      <Link 
        to="/" 
        className="absolute top-4 left-4 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>{t('back')}</span>
      </Link>

      {/* Language selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="ghost" />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700/50 bg-card/95 backdrop-blur">
        <CardHeader className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg" 
            alt="EGNS Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <CardTitle className="font-heading text-2xl">{t('register')}</CardTitle>
          <CardDescription>Create your EGNS German account</CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="activationCode">Activation Code <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="activationCode"
                  type="text"
                  placeholder=""
                  value={formData.activationCode}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="activation-code-input"
                />
              </div>
              
              {/* Buy Code Section */}
              <div className="bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/30 rounded-lg p-3 mt-2">
                <a 
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-teal-400 mb-2 hover:underline cursor-pointer block"
                  data-testid="contact-purchase-link"
                >
                  Don't have a code? Contact us to purchase
                </a>
                <div className="flex flex-col sm:flex-row gap-2">
                  <a 
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                    data-testid="whatsapp-buy-btn"
                  >
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full bg-green-600 hover:bg-green-700 border-green-600 text-white"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                  <a 
                    href={phoneUrl}
                    className="flex-1"
                    data-testid="phone-buy-btn"
                  >
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Call Us
                    </Button>
                  </a>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">{phoneDisplay}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">{t('name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Ahmed Mohamed"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('language')}</Label>
              <Select 
                value={formData.preferredLanguage} 
                onValueChange={(value) => setFormData({ ...formData, preferredLanguage: value })}
              >
                <SelectTrigger data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                  <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                  <SelectItem value="ar">🇪🇬 العربية</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-teal-500 hover:bg-teal-600" 
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('register')
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t('orContinueWith')}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="googleActivationCode" className="text-xs">Activation Code for Google Sign-up</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="googleActivationCode"
                  type="text"
                  placeholder=""
                  value={formData.activationCode}
                  onChange={(e) => setFormData({ ...formData, activationCode: e.target.value })}
                  className="pl-10 text-sm"
                  data-testid="google-activation-code-input"
                />
              </div>
            </div>
            {isWebView && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center" data-testid="webview-warning-register">
                <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
                  You're using {getInAppBrowserName()}'s browser
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  Google Sign-In doesn't work here. Use email/password or open in your browser:
                </p>
                <Button type="button" size="sm" variant="outline" className="text-xs border-amber-500/40 text-amber-600" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied! Paste it in Chrome or Safari.'); }}>
                  <ExternalLink className="h-3 w-3 mr-1" /> Copy link to open in browser
                </Button>
              </div>
            )}
            <Button 
              variant="outline" 
              className={`w-full ${isWebView ? 'opacity-50' : ''}`}
              onClick={handleGoogleLogin}
              data-testid="google-register-btn"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {t('google')}
            </Button>
          </div>
        </CardContent>

        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            {t('haveAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
              {t('login')}
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
